import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loader from "../../../components/Loader/Loader";
import CredentialExpiryAlert from "../../../components/dr/CredentialExpiryAlert";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import { drApi } from "../../../services/drApi";
import { useRole } from "../../../hooks/useRole";

const CREDENTIAL_TYPES = [
  { value: "CCO", label: "CCO (Chief Compliance Officer)" },
  { value: "EMD", label: "EMD (Exempt Market Dealer)" },
  { value: "UDP", label: "UDP (Ultimate Designated Person)" },
  { value: "OTHER", label: "Other regulatory certification" },
];

const STATUS_CLASS = {
  APPROVED: "s-active",
  PENDING:  "s-rev",
  EXPIRED:  "s-fail",
  REJECTED: "s-fail",
};

const initialForm = () => ({
  credential_type: "CCO",
  license_number: "",
  issuer: "",
  issue_date: "",
  expiry_date: "",
  document_url: "",
});

function formatExpiry(dateStr) {
  const d = new Date(dateStr);
  if (!Number.isFinite(d.getTime())) return "—";
  const daysLeft = Math.ceil((d.getTime() - Date.now()) / (86400 * 1000));
  const formatted = d.toLocaleDateString();
  if (daysLeft < 0) return `${formatted} (expired ${-daysLeft}d ago)`;
  if (daysLeft <= 30) return `${formatted} (${daysLeft}d left)`;
  return formatted;
}

function DRCredentials() {
  // Every session that decides (DR, CCO, UDP) files its own credential here:
  // a CCO and a UDP each carry a registration of their own, so self-service is
  // not retired, it is wider than it was. A Platform Manager holding no
  // compliance role has nothing to file and would 403 on the personal fetch,
  // so it gets a pointer to the review queue instead.
  const { canDecide } = useRole();

  const [loading, setLoading] = useState(canDecide);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);

  useEffect(() => {
    if (canDecide) fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchRows() {
    setLoading(true);
    try {
      const res = await drApi.myCredentials();
      setRows(Array.isArray(res?.data) ? res.data : []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Fetch failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMsg(null);

    const trimmedUrl = form.document_url.trim();
    if (trimmedUrl) {
      try {
        const u = new URL(trimmedUrl);
        if (u.protocol !== "http:" && u.protocol !== "https:") {
          throw new Error("bad_protocol");
        }
      } catch (_) {
        setSubmitMsg({ tone: "danger", text: "Document URL must start with http:// or https://" });
        setSubmitting(false);
        return;
      }
    }

    try {
      await drApi.createCredential({
        credential_type: form.credential_type,
        license_number: form.license_number.trim(),
        issuer: form.issuer.trim() || undefined,
        issue_date: form.issue_date || undefined,
        expiry_date: form.expiry_date,
        document_url: trimmedUrl || undefined,
      });
      setSubmitMsg({ tone: "info", text: "Credential submitted for Platform Manager review." });
      setForm(initialForm());
      await fetchRows();
    } catch (err) {
      setSubmitMsg({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Submit failed",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Credentials" />

      {!canDecide ? (
        <section className="card">
          <div className="card__body">
            <Alert tone="info">
              This page files your own reviewer credential. Review other
              people's credentials under{" "}
              <Link
                to="/ad/dr-oversight/credentials"
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                Compliance, DR credentials
              </Link>
              .
            </Alert>
          </div>
        </section>
      ) : (
        <>
          <CredentialExpiryAlert />

          <section className="card">
            <div className="card__head">
              <div className="card__title">Submit a new credential</div>
            </div>
            <div className="card__body">
              <form onSubmit={handleSubmit}>
                <div className="form-grid form-grid--2col">
                  <Field
                    as="select"
                    label="Type *"
                    name="credential_type"
                    value={form.credential_type}
                    onChange={(e) => setForm({ ...form, credential_type: e.target.value })}
                  >
                    {CREDENTIAL_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Field>
                  <Field
                    label="License number *"
                    name="license_number"
                    maxLength={64}
                    value={form.license_number}
                    onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                    required
                  />
                  <Field
                    label="Issuer"
                    name="issuer"
                    maxLength={128}
                    value={form.issuer}
                    onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                    placeholder="e.g. OSC"
                  />
                  <Field
                    label="Issue date"
                    name="issue_date"
                    type="date"
                    max={today}
                    value={form.issue_date}
                    onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                  />
                  <Field
                    label="Expiry date *"
                    name="expiry_date"
                    type="date"
                    min={today}
                    value={form.expiry_date}
                    onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                    required
                  />
                  <Field
                    label="Document URL (Azure Blob)"
                    name="document_url"
                    type="url"
                    maxLength={512}
                    value={form.document_url}
                    onChange={(e) => setForm({ ...form, document_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>

                {submitMsg && <div style={{ marginTop: 12 }}><Alert tone={submitMsg.tone}>{submitMsg.text}</Alert></div>}

                <div style={{ marginTop: 12 }}>
                  <Btn variant="primary" type="submit" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit for review"}
                  </Btn>
                </div>
              </form>
            </div>
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__title">My credentials</div>
            </div>
            <div className="card__body card__body--flush">
              {error && <div style={{ padding: "12px 14px" }}><Alert tone="danger">{error}</Alert></div>}
              {!error && rows.length === 0 ? (
                <Empty>No credentials on file yet.</Empty>
              ) : rows.length > 0 ? (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>License #</th>
                      <th>Issuer</th>
                      <th>Expires</th>
                      <th>Status</th>
                      <th>Reviewed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.credential_type}</td>
                        <td className="mono">{r.license_number}</td>
                        <td className="muted">{r.issuer || "—"}</td>
                        <td>{r.expiry_date ? formatExpiry(r.expiry_date) : "—"}</td>
                        <td>
                          <span className={`tbl-status ${STATUS_CLASS[r.status] || "s-mute"}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="muted">
                          {r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}

export default DRCredentials;
